const EVENTOS_ATIVIDADE = ["pointerdown", "pointermove", "keydown", "wheel", "touchstart", "touchmove"] as const;

function chaveAtividade(codLogin: number): string {
  return `auth-ultima-atividade:${codLogin}`;
}

function lerAtividade(chave: string): number | undefined {
  try {
    const valor = window.localStorage.getItem(chave);
    if (valor === null) return undefined;
    const instante = Number(valor);
    return Number.isFinite(instante) && instante >= 0 ? Math.min(instante, Date.now()) : undefined;
  } catch {
    return undefined;
  }
}

function gravarAtividade(chave: string, instante: number): void {
  try {
    window.localStorage.setItem(chave, String(instante));
  } catch {
    // Sem armazenamento disponível, o controle continua funcionando nesta aba.
  }
}

/** Somente uma autenticação concluída inicia um novo prazo; recargas preservam a atividade. */
export function registrarInicioSessao(codLogin: number): void {
  gravarAtividade(chaveAtividade(codLogin), Date.now());
}

/** Compartilha apenas timestamps entre abas, sem armazenar credenciais ou dados da sessão. */
export function monitorarInatividadeSessao(codLogin: number, aoExpirar: () => void, minutos = 30): () => void {
  const limiteInatividadeMs = (Number.isInteger(minutos) && minutos >= 1 && minutos <= 1440 ? minutos : 30) * 60 * 1000;
  const chave = chaveAtividade(codLogin);
  let ultimaAtividade = lerAtividade(chave) ?? Date.now();
  let ultimaGravacao = 0;
  let encerrado = false;
  let timer: number | undefined;
  let timerGravacao: number | undefined;
  if (lerAtividade(chave) === undefined) gravarAtividade(chave, ultimaAtividade);

  function verificarPrazo() {
    window.clearTimeout(timer);
    if (encerrado) return;
    const compartilhada = lerAtividade(chave);
    if (compartilhada !== undefined) {
      ultimaAtividade = compartilhada === 0 ? 0 : Math.max(ultimaAtividade, compartilhada);
    }
    const restante = limiteInatividadeMs - (Date.now() - ultimaAtividade);
    if (ultimaAtividade === 0 || restante <= 0) {
      encerrado = true;
      window.clearTimeout(timerGravacao);
      // Zero comunica o encerramento às outras abas e impede restaurar a sessão ao recarregar.
      gravarAtividade(chave, 0);
      aoExpirar();
      return;
    }
    timer = window.setTimeout(verificarPrazo, restante);
  }

  function salvarAtividade() {
    timerGravacao = undefined;
    if (encerrado) return;
    const compartilhada = lerAtividade(chave);
    if (compartilhada === 0) {
      verificarPrazo();
      return;
    }
    // Uma gravação pendente não desfaz o logout nem a atividade mais recente de outra aba.
    if (compartilhada !== undefined) ultimaAtividade = Math.max(ultimaAtividade, compartilhada);
    ultimaGravacao = Date.now();
    gravarAtividade(chave, ultimaAtividade);
  }

  function registrarAtividade(evento: Event) {
    // Eventos de scripts e chamadas automáticas de API não renovam o prazo.
    if (encerrado || !evento.isTrusted) return;
    const agora = Date.now();
    if (agora - ultimaAtividade >= limiteInatividadeMs) {
      verificarPrazo();
      if (encerrado) return;
    }
    ultimaAtividade = agora;
    // Limita gravações durante movimentos contínuos e persiste também a última interação.
    if (agora - ultimaGravacao >= 1000) {
      window.clearTimeout(timerGravacao);
      salvarAtividade();
    } else if (timerGravacao === undefined) {
      timerGravacao = window.setTimeout(salvarAtividade, 1000 - (agora - ultimaGravacao));
    }
  }

  function sincronizarAbas(evento: StorageEvent) {
    if (evento.key !== chave) return;
    const compartilhada = lerAtividade(chave);
    if (compartilhada === undefined) return;
    ultimaAtividade = compartilhada === 0 ? 0 : Math.max(ultimaAtividade, compartilhada);
    if (compartilhada === 0) verificarPrazo();
  }

  function aoRetornar() {
    if (document.visibilityState === "visible") verificarPrazo();
  }

  EVENTOS_ATIVIDADE.forEach((evento) => window.addEventListener(evento, registrarAtividade, { passive: true, capture: true }));
  window.addEventListener("storage", sincronizarAbas);
  window.addEventListener("focus", aoRetornar);
  document.addEventListener("visibilitychange", aoRetornar);
  verificarPrazo();

  return () => {
    window.clearTimeout(timer);
    window.clearTimeout(timerGravacao);
    if (!encerrado && timerGravacao !== undefined) salvarAtividade();
    encerrado = true;
    EVENTOS_ATIVIDADE.forEach((evento) => window.removeEventListener(evento, registrarAtividade, true));
    window.removeEventListener("storage", sincronizarAbas);
    window.removeEventListener("focus", aoRetornar);
    document.removeEventListener("visibilitychange", aoRetornar);
  };
}
